import { useMemo, useState } from "react";
import TimelineBlock from "./TimelineBlock";

export default function TechnicianRow({
  row,
  hours,
  onOpen,
  onDropCard,
  onDragStart,
  pixelsPerHour = 96,
}) {
  const [hoverLeft, setHoverLeft] = useState(null);
  const startMinutes = useMemo(() => parseHourLabelToMinutes(hours[0] || "07:00"), [hours]);

  const resolveDropSlot = (event) => {
    const container = event.currentTarget;
    const rect = container.getBoundingClientRect();
    const relativeX = event.clientX - rect.left + container.scrollLeft;
    const snappedSegments = Math.max(0, Math.round(relativeX / (pixelsPerHour / 2)));
    const snappedLeft = snappedSegments * (pixelsPerHour / 2);
    const totalMinutes = startMinutes + snappedSegments * 30;
    return {
      left: snappedLeft,
      plannedStartTime: minutesToTimeLabel(totalMinutes),
    };
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    const slot = resolveDropSlot(event);
    setHoverLeft(slot.left);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData("application/logboek24-werkbon");
    setHoverLeft(null);
    if (!raw) return;
    const payload = JSON.parse(raw);
    const slot = resolveDropSlot(event);
    onDropCard(payload, row, slot);
  };

  const rowTone =
    row.conflictCount > 0 || row.capacityTone === "over"
      ? "border-rose-100 bg-rose-50/40"
      : row.capacityTone === "warning"
      ? "border-amber-100 bg-amber-50/30"
      : "border-slate-100 bg-white";

  return (
    <div className={`grid min-h-[90px] grid-cols-[220px_minmax(0,1fr)] border-b last:border-b-0 ${rowTone}`}>
      <div className="border-r border-slate-100 bg-white/80 px-4 py-4">
        <p className="font-semibold text-slate-950">{row.name}</p>
        <p className="mt-1 text-xs text-slate-500">
          {row.assignmentCount} item(s) • {row.totalHours.toFixed(1)} uur
        </p>
        <div className="mt-2">
          <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
            <div
              className={`h-full rounded-full ${capacityBarClass(row.capacityTone)}`}
              style={{ width: `${Math.min(row.occupancyRatio, 1.2) * 100}%` }}
            />
          </div>
          <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
            <span className="font-medium text-slate-600">{row.capacityLabel}</span>
            {row.conflictCount > 0 ? (
              <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 font-semibold text-rose-700">
                {row.conflictCount} conflict(en)
              </span>
            ) : (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                Capaciteit OK
              </span>
            )}
          </div>
        </div>
      </div>

      <div
        className="relative overflow-x-auto bg-white"
        onDragOver={handleDragOver}
        onDragLeave={() => setHoverLeft(null)}
        onDrop={handleDrop}
      >
        <div className="relative min-w-[960px]" style={{ width: hours.length * pixelsPerHour }}>
          <div className="grid" style={{ gridTemplateColumns: `repeat(${hours.length}, minmax(0, 1fr))` }}>
            {hours.map((hour) => (
              <div
                key={`${row.id}-${hour}`}
                className="border-r border-slate-100 px-2 py-2 text-xs text-slate-400"
              >
                {hour}
              </div>
            ))}
          </div>

          <div
            className="relative border-t border-slate-100 bg-slate-50/40"
            style={{ height: 60 }}
          >
            {hours.map((hour, index) => (
              <div
                key={`${row.id}-grid-${hour}`}
                className="absolute bottom-0 top-0 border-r border-slate-100"
                style={{ left: index * pixelsPerHour, width: pixelsPerHour }}
              />
            ))}

            {hoverLeft !== null ? (
              <div
                className="absolute bottom-0 top-0 z-10 w-0 border-l-2 border-dashed border-slate-400"
                style={{ left: hoverLeft }}
              />
            ) : null}

            {row.items.map((item) => (
              <TimelineBlock
                key={`${row.id}-${item.id}`}
                item={item}
                left={item.timelineLeft}
                width={item.timelineWidth}
                onOpen={onOpen}
                onDragStart={onDragStart}
                colorClass={item.timelineColor}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function parseHourLabelToMinutes(label) {
  const [hours, minutes] = String(label || "07:00").split(":");
  return Number(hours || 7) * 60 + Number(minutes || 0);
}

function minutesToTimeLabel(totalMinutes) {
  const normalized = Math.max(0, Number(totalMinutes || 0));
  const hours = `${Math.floor(normalized / 60)}`.padStart(2, "0");
  const minutes = `${normalized % 60}`.padStart(2, "0");
  return `${hours}:${minutes}`;
}

function capacityBarClass(tone) {
  if (tone === "over") return "bg-rose-500";
  if (tone === "warning") return "bg-amber-500";
  return "bg-emerald-500";
}
